import * as XLSX from 'xlsx';
import { MESSAGES } from '@/constants/messages'
import { RequiredColumn, ColumnDataType, SpreadsheetTemplate } from '@/constants/templateSchema';

export const convertDataToCsvString = (headers: string[], rows: any[][]) => {
  const aoa = [headers, ...rows];

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  return XLSX.utils.sheet_to_csv(worksheet);
};

export const exportExcel = (fileName: string, columns: RequiredColumn[], rows: any[][]) => {
  const headers = columns.map(column => column.header);
  
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  const textColumnIndices = columns
    .map((col, index) => (col.forceTextFormat ? index : -1))
    .filter(index => index !== -1);

  rows.forEach((_, rIdx) => {
    const R = rIdx + 1; 

    textColumnIndices.forEach(C => {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      
      if (worksheet[addr]) {
        worksheet[addr].t = 's';
        worksheet[addr].z = '@';
        
        if (worksheet[addr].v !== null && worksheet[addr].v !== undefined) {
            worksheet[addr].v = String(worksheet[addr].v);
        }
      }
    });
  });

  worksheet['!cols'] = columns.map(() => ({ wch: 22 }));

  const workbook = XLSX.utils.book_new();
  const safeSheetName = fileName.replace(/[\[\]\?\/\*\\\:]/g, '').substring(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);


  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const timeStr = now.getHours().toString().padStart(2, '0') + '-' + now.getMinutes().toString().padStart(2, '0');
  
  const filename = `${fileName}-export-${dateStr}-${timeStr}.xlsx`;
  
  XLSX.writeFile(workbook, filename);
};

const hasActualContent = (value: any): boolean => {
  if (value === null || value === undefined) return false;

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (typeof value === 'number') return true;

  return true;
};


export const createTemplate = (template: SpreadsheetTemplate) => {
  const worksheet = XLSX.utils.aoa_to_sheet([template.columns.map(col => col.header)]);
  
  const rowsToFormat = 10000;

  template.columns.forEach((column, colIdx) => {
    if (column.forceTextFormat) {
      for (let R = 1; R <= rowsToFormat; R++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: colIdx });
        worksheet[addr] = { v: '', t: 's', z: '@' };
      }
    }
  });

  worksheet['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: rowsToFormat, c: template.columns.length - 1 }
  });

  worksheet['!cols'] = template.columns.map(() => ({ wch: 25 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
  XLSX.writeFile(workbook, template.name.endsWith('.xlsx') ? template.name : `${template.name}.xlsx`);
};

export const parseTemplate = async (
  file: File, 
  columns: RequiredColumn[]
): Promise<ExcelParseResult> => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { 
    type: 'array',
    cellDates: true 
  });

  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  
  const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { 
    header: 1, 
    defval: '' 
  });

  if (rows.length <= 1) {
    return { success: false, error: MESSAGES.ERRORS.FILE.EMPTY };
  }

  const fileHeaders = rows[0].map(h => String(h).trim().toLowerCase());
  const targetIndices = columns.map(required => 
    fileHeaders.indexOf(required.header.toLowerCase())
  );

  const missingColumns = columns.filter((_, idx) => targetIndices[idx] === -1);
  if (missingColumns.length > 0) {
    return { 
      success: false, 
      error: MESSAGES.ERRORS.FILE.MISSING_COLUMNS(missingColumns.map(c => c.header))
    };
  }

  const dataRows = rows.slice(1).filter(row => {
    return targetIndices.some(index => {
      const cellValue = row[index];
      return hasActualContent(cellValue);
    });
  });

  if (dataRows.length === 0) {
    return { 
      success: false, 
      error: MESSAGES.ERRORS.FILE.EMPTY 
    };
  }

  return { 
    success: true, 
    data: { 
      headers: columns.map(column => column.header), 
      rows: dataRows 
    } 
  };
};

export const isExcelFileType = async (file: File) => {
  if (!file) return false;

  const excelMimeTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];
  
  const fileName = file.name.toLowerCase();
  const isExcelExtension = fileName.endsWith('.xls') || fileName.endsWith('.xlsx');
  const isExcelMimeType = excelMimeTypes.includes(file.type);

  if (!isExcelMimeType && !isExcelExtension)
    return false;

  return true;
};

export const getIndexesForType = (columns: RequiredColumn[], type: ColumnDataType): number[] => {
  return columns
    .map((col, index) => col.type === type ? index : -1)
    .filter(index => index !== -1);
};

interface ExcelParseData {
  headers: string[];
  rows: any[][];
}

type ExcelParseResult = 
  | { success: true; data: ExcelParseData }
  | { success: false; error: string };